import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  AlertTriangle,
  FileText,
  DollarSign,
  Briefcase,
  Heart,
  Home,
  Users,
  CheckCircle,
  Loader2,
  Sparkles,
  Info
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Debtor, Case, ExceptionRequest } from '../types';

interface HardshipPageProps {
  debtor: Debtor;
}

interface AIEvaluation {
  verified: boolean;
  category: string;
  severityScore: number;
  recommendedRelief: string;
  reliefTerms?: string;
  approvalRecommendation: string;
  confidenceScore: number;
}

const HARDSHIP_REASONS = [
  { value: 'job_loss', label: 'Job Loss / Unemployment', icon: Briefcase },
  { value: 'medical', label: 'Medical Emergency / Illness', icon: Heart },
  { value: 'income_reduction', label: 'Reduced Income', icon: DollarSign },
  { value: 'housing', label: 'Housing Issues / Eviction Risk', icon: Home },
  { value: 'family', label: 'Family Emergency', icon: Users },
  { value: 'other', label: 'Other Financial Hardship', icon: AlertTriangle },
];

const RELIEF_LABELS: Record<string, string> = {
  payment_pause: 'Payment Pause (30-90 days)',
  reduced_payments: 'Reduced Payment Amount',
  extended_term: 'Extended Repayment Period',
  interest_waiver: 'Interest Waiver',
  settlement: 'Settlement Offer',
  write_off: 'Write-off Consideration',
};

export default function HardshipPage({ debtor }: HardshipPageProps) {
  const [cases, setCases] = useState<Case[]>([]);
  const [existingRequests, setExistingRequests] = useState<ExceptionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [aiEvaluation, setAiEvaluation] = useState<AIEvaluation | null>(null);
  
  // Form state
  const [selectedCase, setSelectedCase] = useState<string>('');
  const [hardshipReason, setHardshipReason] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [requestedAction, setRequestedAction] = useState<string>('reduced_payment');
  const [proposedAmount, setProposedAmount] = useState<string>('');
  const [supportingInfo, setSupportingInfo] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [debtor.debtor_id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load cases
      const casesResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE debtor_id = '${debtor.debtor_id}' AND status NOT IN ('paid_in_full', 'settled', 'write_off') ORDER BY total_amount DESC`
      );
      setCases(casesResult);
      if (casesResult.length > 0) {
        setSelectedCase(casesResult[0].case_id);
      }

      // Load existing hardship requests
      const requestsResult = await serenityClient.queryData<ExceptionRequest>(
        `SELECT * FROM ExceptionRequests WHERE debtor_id = '${debtor.debtor_id}' ORDER BY created_at DESC`
      );
      setExistingRequests(requestsResult);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCase || !hardshipReason || !description) return;

    setIsSubmitting(true);
    setIsEvaluating(true);

    try {
      // Generate request ID
      const requestId = `REQ-${Date.now()}`;
      
      // Step 1: Get AI pre-evaluation
      let evaluation: AIEvaluation | null = null;
      try {
        evaluation = await serenityClient.evaluateHardship(
          requestId,
          debtor.debtor_id,
          selectedCase
        );
        setAiEvaluation(evaluation);
      } catch (evalError) {
        console.warn('AI evaluation failed, continuing with submission:', evalError);
      }
      
      setIsEvaluating(false);

      // Step 2: Create the hardship request with AI recommendation
      const newRequest: Partial<ExceptionRequest> = {
        request_id: requestId,
        case_id: selectedCase,
        debtor_id: debtor.debtor_id,
        request_type: 'hardship',
        reason: hardshipReason,
        description: description,
        requested_action: evaluation?.recommendedRelief || requestedAction,
        proposed_amount: proposedAmount ? parseFloat(proposedAmount) : undefined,
        supporting_info: supportingInfo || undefined,
        status: evaluation?.approvalRecommendation === 'approve' ? 'under_review' : 'pending',
        ai_category: evaluation?.category,
        ai_severity_score: evaluation?.severityScore,
        ai_recommendation: evaluation?.recommendedRelief,
        ai_confidence: evaluation?.confidenceScore,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      await serenityClient.insertData('ExceptionRequests', newRequest);

      // Create automatic workflow task for hardship review
      try {
        const taskId = `TASK-${Date.now()}`;
        const now = new Date().toISOString();
        await serenityClient.insertData('WorkflowTasks', {
          task_id: taskId,
          workflow_type: 'hardship_review',
          entity_type: 'exception_request',
          entity_id: requestId,
          case_id: selectedCase,
          debtor_id: debtor.debtor_id,
          task_type: 'hardship_evaluation',
          title: 'Hardship Request Review',
          description: `Review hardship request for ${hardshipReason}`,
          status: evaluation?.confidenceScore && evaluation.confidenceScore >= 0.85 ? 'awaiting_input' : 'pending',
          priority: 'high',
          assigned_role: 'collections_agent',
          created_by: 'system',
          ai_agent_code: 'dfs-evaluador',
          ai_recommendation: evaluation?.recommendedRelief,
          ai_confidence: evaluation?.confidenceScore,
          requires_approval: true,
          created_at: now,
          updated_at: now,
        });
        console.log('[Workflow] Created hardship review task:', taskId);
      } catch (taskError) {
        console.warn('Failed to create workflow task:', taskError);
      }

      // Reset form
      setHardshipReason('');
      setDescription('');
      setRequestedAction('reduced_payment');
      setProposedAmount('');
      setSupportingInfo('');
      
      setSubmitSuccess(true);
      
      // Reload requests
      loadData();
    } catch (error) {
      console.error('Failed to submit request:', error);
      alert('Failed to submit hardship request. Please try again.');
    } finally {
      setIsSubmitting(false);
      setIsEvaluating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'denied':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-lg text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Request Submitted!</h2>
          <p className="text-gray-500 mb-4">
            Your hardship request has been submitted and will be reviewed by our team.
          </p>
          
          {/* AI Evaluation Summary */}
          {aiEvaluation && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-purple-700 mb-2">
                <Sparkles className="w-4 h-4" />
                <span className="font-medium">AI Pre-Assessment</span>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-purple-600">
                  <strong>Category:</strong> {aiEvaluation.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-purple-600">
                  <strong>Severity Score:</strong> {aiEvaluation.severityScore}/100
                </p>
                <p className="text-purple-600">
                  <strong>Recommended Relief:</strong> {RELIEF_LABELS[aiEvaluation.recommendedRelief] || aiEvaluation.recommendedRelief}
                </p>
                <p className="text-purple-600">
                  <strong>Initial Assessment:</strong>{' '}
                  <span className={aiEvaluation.approvalRecommendation === 'approve' ? 'text-green-600' : 'text-yellow-600'}>
                    {aiEvaluation.approvalRecommendation === 'approve' ? 'Likely Eligible' : 'Under Review'}
                  </span>
                </p>
              </div>
            </div>
          )}
          
          <p className="text-gray-400 text-sm mb-6">
            You'll receive a notification once a final decision is made.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/dashboard"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
            <button
              onClick={() => {
                setSubmitSuccess(false);
                setAiEvaluation(null);
              }}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to="/dashboard"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Financial Hardship</h1>
                <p className="text-xs text-gray-500">Request special consideration</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : cases.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Cases</h2>
            <p className="text-gray-500 mb-4">You don't have any active cases that require a hardship request.</p>
            <Link
              to="/dashboard"
              className="text-blue-600 hover:underline"
            >
              Return to Dashboard
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Submit Hardship Request</h2>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-orange-800">
                    If you're experiencing financial difficulties that make it hard to pay your debt,
                    you may qualify for special payment arrangements. Please provide as much detail
                    as possible to help us understand your situation.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Case Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Case *
                    </label>
                    <select
                      value={selectedCase}
                      onChange={(e) => setSelectedCase(e.target.value)}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {cases.map((c) => (
                        <option key={c.case_id} value={c.case_id}>
                          {c.case_id} - {formatCurrency(c.total_amount - (c.amount_paid || 0))} remaining
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Hardship Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Hardship *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {HARDSHIP_REASONS.map((reason) => {
                        const Icon = reason.icon;
                        return (
                          <button
                            key={reason.value}
                            type="button"
                            onClick={() => setHardshipReason(reason.value)}
                            className={`p-3 border-2 rounded-lg text-left transition-colors ${
                              hardshipReason === reason.value
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <Icon className={`w-5 h-5 mb-1 ${
                              hardshipReason === reason.value ? 'text-orange-600' : 'text-gray-400'
                            }`} />
                            <p className={`text-sm font-medium ${
                              hardshipReason === reason.value ? 'text-orange-900' : 'text-gray-700'
                            }`}>
                              {reason.label}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Describe Your Situation *
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      required
                      placeholder="Please explain your current financial situation and why you need assistance..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>

                  {/* Requested Action */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      What Would Help You? *
                    </label>
                    <select
                      value={requestedAction}
                      onChange={(e) => setRequestedAction(e.target.value)}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="reduced_payment">Reduced Monthly Payment</option>
                      <option value="payment_pause">Temporary Payment Pause</option>
                      <option value="settlement">Settlement Offer</option>
                      <option value="extended_plan">Extended Payment Plan</option>
                      <option value="interest_reduction">Interest Rate Reduction</option>
                      <option value="other">Other (explain below)</option>
                    </select>
                  </div>

                  {/* Proposed Amount */}
                  {(requestedAction === 'reduced_payment' || requestedAction === 'settlement') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proposed Amount (optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={proposedAmount}
                          onChange={(e) => setProposedAmount(e.target.value)}
                          placeholder={requestedAction === 'settlement' ? 'Total settlement amount' : 'Monthly payment amount'}
                          className="w-full pl-8 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Supporting Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Information (optional)
                    </label>
                    <textarea
                      value={supportingInfo}
                      onChange={(e) => setSupportingInfo(e.target.value)}
                      rows={3}
                      placeholder="Any additional details that might help us understand your situation better..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    />
                  </div>
                  
                  {/* AI Info */}
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600 mt-0.5" />
                    <div className="text-sm text-purple-700">
                      <p className="font-medium">AI-Powered Review</p>
                      <p className="text-purple-600 text-xs mt-1">
                        Your request will be pre-evaluated by AI to recommend the most appropriate relief option and speed up the review process.
                      </p>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={!selectedCase || !hardshipReason || !description || isSubmitting}
                    className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {isEvaluating ? 'AI Evaluating...' : 'Submitting...'}
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-5 h-5" />
                        Submit Hardship Request
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Existing Requests */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Requests</h3>
                {existingRequests.length === 0 ? (
                  <p className="text-sm text-gray-500">No previous requests.</p>
                ) : (
                  <div className="space-y-3">
                    {existingRequests.map((req) => (
                      <div key={req.request_id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{req.case_id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(req.status)}`}>
                            {getStatusLabel(req.status)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {HARDSHIP_REASONS.find(r => r.value === req.reason)?.label || req.reason}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(req.created_at)}</p>
                        {req.decision_notes && (
                          <p className="text-xs text-blue-600 mt-2 bg-blue-50 p-2 rounded">
                            {req.decision_notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tips */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3">Tips for Your Request</h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Upload supporting documents to strengthen your case</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Be specific about your current income and expenses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Propose a realistic amount you can afford</span>
                  </li>
                </ul>
                <Link
                  to="/documents"
                  className="mt-4 inline-block text-sm text-blue-600 hover:underline"
                >
                  Upload documents →
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
